/**
 * Google tags — BASIC Consent Mode loader.
 * -----------------------------------------
 * gtag.js laddas ALDRIG förrän användaren gett samtycke. Vid "Neka alla"
 * (statistics=false & marketing=false) laddas ingenting och ingen
 * trackingdata skickas till Google.
 *
 *   • statistics=true → GA4 (G-9BQ7RR4SWV) aktiveras
 *   • marketing=true  → Google Ads-destinationerna aktiveras
 *
 * Destinationerna återanvänder EXAKT befintlig/verifierad produktionsconfig:
 *   - GA4-id och den extra Ads-destinationen är samma som tidigare låg
 *     statiskt i public/index.html.
 *   - Primär Google Ads-id läses från REACT_APP_GOOGLE_ADS_ID (ingen ny
 *     hårdkodning). Conversion-id/labels rörs inte.
 *
 * Samma consent-källa som resten av sajten (utils/cookieConsent.js) — inget
 * parallellt samtyckessystem. Idempotent: laddar aldrig gtag.js två gånger
 * och konfigurerar aldrig samma destination mer än en gång.
 */
import { getConsent, onConsentChange } from "@/utils/cookieConsent";
import { logGa4Config } from "@/utils/nyHemsidaVisit";

// Befintliga, verifierade destinationer (oförändrade).
const GA4_ID = "G-9BQ7RR4SWV";
const ADS_PRIMARY = process.env.REACT_APP_GOOGLE_ADS_ID || "";
const ADS_SECONDARY = "AW-18361891057";

let gtagScriptLoaded = false;
const configured = new Set();

function ensureGtagBase() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
}

function loadGtagScript(primaryId) {
  if (gtagScriptLoaded) return;
  gtagScriptLoaded = true;
  ensureGtagBase();
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(primaryId);
  document.head.appendChild(s);
  window.gtag("js", new Date());
}

function configureGa4() {
  if (configured.has(GA4_ID)) return;
  loadGtagScript(GA4_ID);
  configured.add(GA4_ID);
  window.gtag("config", GA4_ID);
  // Passiv diagnostik EFTER det befintliga GA4-config-anropet. Ändrar varken
  // gating eller exekveringsordning; loggar bara att GA4 config kördes.
  logGa4Config();
}

function configureAds() {
  const ids = [ADS_PRIMARY, ADS_SECONDARY].filter(
    (id) => id && id.indexOf("REACT_APP") === -1
  );
  if (!ids.length) return;
  loadGtagScript(ids[0]);
  for (const id of ids) {
    if (configured.has(id)) continue;
    configured.add(id);
    window.gtag("config", id);
  }
}

function applyConsent(consent) {
  if (!consent) return;
  if (consent.statistics) configureGa4();
  if (consent.marketing) configureAds();
}

export function initGoogleTags() {
  if (typeof window === "undefined") return;
  try {
    applyConsent(getConsent());
  } catch {
    /* aldrig krascha på tracking */
  }
  onConsentChange((e) => {
    try {
      const c = e && e.detail ? e.detail : getConsent();
      applyConsent(c);
    } catch {
      /* ignore */
    }
  });
}
